<template>
  <v-dialog
    v-model="open"
    fullscreen
    persistent
    :retain-focus="false"
  >
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-btn
          icon="fa-solid fa-xmark"
          title="Fechar"
          @click="close"
        />
        <v-toolbar-title>Editar cadeiras · {{ title }}</v-toolbar-title>

        <template v-if="editor">
          <v-menu location="bottom end" :disabled="!editor.pendingCount">
            <template #activator="{ props: menu }">
              <v-chip
                v-bind="menu"
                class="mr-4"
                variant="flat"
                :color="editor.pendingCount ? 'warning' : undefined"
              >
                {{ editor.pendingCount ? plural(editor.pendingCount, 'alteração pendente', 'alterações pendentes') : 'Nada pendente' }}
              </v-chip>
            </template>
            <v-card max-width="420" max-height="420" class="overflow-y-auto">
              <v-list density="compact">
                <v-list-item
                  v-for="(change, index) in editor.changes"
                  :key="index"
                  :prepend-icon="CHANGE_ICONS[change.kind]"
                  :title="change.text"
                />
              </v-list>
            </v-card>
          </v-menu>
          <v-btn
            variant="text"
            class="text-none"
            :disabled="!editor.pendingCount || loader.loading"
            @click="discard"
          >
            Descartar
          </v-btn>
          <v-btn
            variant="flat"
            color="white"
            class="text-none mr-2"
            prepend-icon="fa-solid fa-floppy-disk"
            :disabled="!editor.pendingCount"
            :loading="loader.loading"
            @click="save"
          >
            Salvar
          </v-btn>
        </template>
      </v-toolbar>

      <v-card-text class="pa-4">
        <v-alert
          v-if="saveError"
          type="error"
          variant="tonal"
          closable
          class="mb-3"
          @click:close="saveError = null"
        >
          Nada foi salvo. {{ saveError }}
        </v-alert>
        <v-alert
          v-if="editor?.renamed.length"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          Renomeadas: {{ editor.renamed.join(', ') }}. O CSV de cadeiras identifica cada cadeira pela fila e pelo nome:
          um arquivo que ainda use o nome antigo recria a cadeira com ele.
        </v-alert>

        <seat-editor
          v-if="editor"
          :key="grid?.id"
          :editor="editor"
          :title="title"
        />
        <div v-else class="d-flex justify-center pa-12">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, shallowRef, watch, type Ref, type ShallowRef } from 'vue'
import { isAxiosError } from 'axios'
import { plural, useSeatEditor, type Change, type SeatEditor as Editor } from '@/helpers/seatEditor/editor'
import type SectionGrid from '@/types/SectionGrid'
import SectionService from '@/services/SectionService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import { useDialog } from '@/stores/dialog'
import SeatEditor from './seat-editor.vue'

interface Props {
  sectionId: string
  /** "Setor › Seção" */
  title: string
}

const { sectionId, title } = defineProps<Props>()

const open = defineModel<boolean>({ required: true })

defineOptions({ name: 'SeatEditorDialog' })

const loader = useLoader()
const snackbar = useSnackBar()
const dialogStore = useDialog()

const CHANGE_ICONS: Record<Change['kind'], string> = {
  create: 'fa-solid fa-plus',
  update: 'fa-solid fa-pen',
  remove: 'fa-solid fa-trash',
}

const grid: Ref<SectionGrid | null> = ref(null)
const editor: ShallowRef<Editor | null> = shallowRef(null)
/** Why the last save was refused (a seat with sales can't be removed, for instance) */
const saveError: Ref<string | null> = ref(null)

watch(open, (isOpen) => {
  if (isOpen) load()
}, { immediate: true })

async function load() {
  grid.value = null
  editor.value = null
  saveError.value = null

  try {
    loader.increment()
    const response = await (new SectionService()).seats(sectionId)
    grid.value = response.data.data
    editor.value = useSeatEditor(response.data.data)
  } catch {
    open.value = false
  } finally {
    loader.decrement()
  }
}

/** The API's validation message, or a generic one */
function messageOf(error: unknown): string {
  if (isAxiosError<{ message?: string, errors?: Record<string, string[]> }>(error)) {
    const errors = Object.values(error.response?.data.errors ?? {}).flat()
    if (errors.length) return errors.join(' ')
    if (error.response?.data.message) return error.response.data.message
  }
  return 'Não foi possível salvar as cadeiras.'
}

async function save() {
  if (!editor.value) return

  try {
    loader.increment()
    const response = await (new SectionService()).saveSeats(sectionId, editor.value.payload())
    editor.value.markSaved(response.data.data)
    saveError.value = null
    snackbar.show('Cadeiras salvas.', 'success')
  } catch (error) {
    // The undo stack is kept, so the change that was refused can be undone and saved again
    saveError.value = messageOf(error)
  } finally {
    loader.decrement()
  }
}

async function discard() {
  if (!editor.value) return

  const confirmed = await dialogStore.open({
    title: 'Descartar alterações',
    message: `Descartar ${plural(editor.value.pendingCount, 'alteração pendente', 'alterações pendentes')}? O Desfazer também é limpo.`,
  })
  if (!confirmed) return

  editor.value.discard()
  saveError.value = null
}

async function close() {
  if (editor.value?.pendingCount) {
    const confirmed = await dialogStore.open({
      title: 'Fechar sem salvar',
      message: `${plural(editor.value.pendingCount, 'alteração pendente será perdida', 'alterações pendentes serão perdidas')}. Fechar mesmo assim?`,
    })
    if (!confirmed) return
  }

  open.value = false
}
</script>
